import type { SVGProps } from "react";

/**
 * Apex marks — Concept A. A geometric N where the right vertical rises above
 * the left, forming an asymmetric peak. Diagonal doubles as the ascent arrow.
 * Gradient flows violet (base) → cyan (apex).
 *
 * Treatments: Primary, MonoLight (on dark), MonoDark (on light), Accent,
 * Outline (for ≤ 20px contexts).
 *
 * Source of truth: tasks/BRAND_IDENTITY.md §2.
 */

type MarkProps = Omit<SVGProps<SVGSVGElement>, "viewBox">;

const Paths = () => (
  <>
    <path d="M 12 52 V 14 H 19 V 52 Z" />
    <path d="M 19 14 L 45 44 V 52 L 19 22 Z" />
    <path d="M 45 52 V 8 L 52 14 V 52 Z" />
  </>
);

export function ApexPrimary({ titleId = "apex-primary-title", ...props }: MarkProps & { titleId?: string }) {
  const gradId = `${titleId}-grad`;
  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" role="img" aria-labelledby={titleId} {...props}>
      <title id={titleId}>NEXIRT</title>
      <defs>
        <linearGradient id={gradId} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#6B4FE6" />
          <stop offset="0.55" stopColor="#6E7FFF" />
          <stop offset="1" stopColor="#22D3EE" />
        </linearGradient>
      </defs>
      <g fill={`url(#${gradId})`}>
        <Paths />
      </g>
    </svg>
  );
}

export function ApexMonoLight(props: MarkProps) {
  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="NEXIRT" {...props}>
      <g fill="#F5F5F5">
        <Paths />
      </g>
    </svg>
  );
}

export function ApexMonoDark(props: MarkProps) {
  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="NEXIRT" {...props}>
      <g fill="#0A0B10">
        <Paths />
      </g>
    </svg>
  );
}

export function ApexAccent(props: MarkProps) {
  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="NEXIRT" {...props}>
      <g fill="#6B4FE6">
        <Paths />
      </g>
    </svg>
  );
}

export function ApexOutline(props: MarkProps) {
  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="NEXIRT" {...props}>
      <g fill="none" stroke="currentColor" strokeWidth={5} strokeLinejoin="miter" strokeMiterlimit={2}>
        <path d="M 15 50 V 17 L 48 46 V 12" />
      </g>
    </svg>
  );
}
